import { createReducer } from 'typesafe-actions'
import {
  GetInstrumentsForCreateAssetResponseDto,
  ReadAssetsRequestDto,
  ReadAssetsResponseDto,
} from '@sparling/financial-common'
import { omit } from 'lodash'
import { ActionTypes } from '../types'
import { LoadingStateEnum } from '../../types'
import {
  getAssetsRequest,
  getAssetsSuccess,
  getAssetsFailure,
  resetAssetsFilters,
  openCreatePairModal,
  createAssetRequest,
  openUpdatePairModal,
  updateAssetRequest,
  getInstrumentsForCreateAssetRequest,
  getInstrumentsForCreateAssetSuccess,
  getInstrumentsForCreateAssetFailure,
} from '../actions'

type InstrumentsForCreateAsset = {
  loading: LoadingStateEnum
  data: GetInstrumentsForCreateAssetResponseDto[]
  error: Error | null
}

type HandleCreateAssetModalData = {
  open: boolean
}

type HandleUpdateAssetModalData = {
  open: boolean
  ids: number[]
}

export type AssetsState = {
  createAssetModal: HandleCreateAssetModalData
  updateAssetModal: HandleUpdateAssetModalData
  instrumentsForCreateAsset: InstrumentsForCreateAsset
  loading: LoadingStateEnum
  data: ReadAssetsResponseDto
  filters: ReadAssetsRequestDto
  error: Error | null
}

export const initialState = {
  createAssetModal: {
    open: false,
  },
  updateAssetModal: {
    open: false,
    ids: [],
  },
  instrumentsForCreateAsset: {
    loading: LoadingStateEnum.IDLE,
    data: [],
    error: null,
  },
  loading: LoadingStateEnum.IDLE,
  data: {
    items: [],
    meta: {
      itemCount: 0,
      totalItems: 0,
      itemsPerPage: 0,
      totalPages: 0,
      currentPage: 0,
    },
  },
  filters: {},
  error: null,
}

export const assets = createReducer<AssetsState, ActionTypes>(initialState)
  .handleAction(getAssetsRequest, (state, { payload }) => ({
    ...state,
    filters: {
      ...state.filters,
      ...payload,
      page: Object.keys(omit(payload, 'page')).length > 0 ? 1 : payload.page,
    },
    loading: LoadingStateEnum.LOADING,
  }))
  .handleAction(getAssetsSuccess, (state, { payload }) => ({
    ...state,
    loading: LoadingStateEnum.SUCCESS,
    data: payload,
  }))
  .handleAction(getAssetsFailure, (state, { payload }) => ({
    ...state,
    loading: LoadingStateEnum.ERROR,
    data: initialState.data,
    error: payload,
  }))
  .handleAction(getInstrumentsForCreateAssetRequest, (state) => ({
    ...state,
    instrumentsForCreateAsset: {
      ...state.instrumentsForCreateAsset,
      loading: LoadingStateEnum.LOADING,
    },
  }))
  .handleAction(getInstrumentsForCreateAssetSuccess, (state, { payload }) => ({
    ...state,
    instrumentsForCreateAsset: {
      ...state.instrumentsForCreateAsset,
      loading: LoadingStateEnum.SUCCESS,
      data: payload,
    },
  }))
  .handleAction(getInstrumentsForCreateAssetFailure, (state, { payload }) => ({
    ...state,
    instrumentsForCreateAsset: {
      ...state.instrumentsForCreateAsset,
      loading: LoadingStateEnum.ERROR,
      error: payload,
    },
  }))
  .handleAction(openCreatePairModal, (state, { payload: { open } }) => ({
    ...state,
    createAssetModal: {
      ...state.createAssetModal,
      open,
    },
  }))
  .handleAction(openUpdatePairModal, (state, { payload: { open, ids } }) => ({
    ...state,
    updateAssetModal: {
      ...state.updateAssetModal,
      open,
      ids,
    },
  }))
  .handleAction([createAssetRequest, updateAssetRequest], (state) => ({
    ...state,
    loading: LoadingStateEnum.LOADING,
  }))
  .handleAction(resetAssetsFilters, (state) => ({
    ...state,
    filters: initialState.filters,
  }))
